<template>
  <div class="meeting-rating f-w f-h">
    <div class="f-h-below-title">
      <div class="meeting-rating__content">
        <h2 class="legend mb10">
          {{ $t("message.RATE_MEETING") }}
        </h2>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div class="field-hint" v-html="$t('sensei.MEETING_EVALUATION_QUESTIONS')" />
        <!-- Made by https://codepen.io/aaroniker/pen/mdyYBPP -->
        <ul class="feedback">
          <div
            v-for="rating in ratings"
            :key="rating.status"
            class="meeting-ratings-mark"
            @click="rate(rating)"
          >
            <li :class="`${rating.status} ${rating.rating == currentRating ? 'active' : ''}`">
              <div>
                <!-- This SVGs represents the rating status, using animated emojis, and are generated with CSS -->
                <svg v-if="rating.status !== 'ok'" class="eye left">
                  <use xlink:href="#eye" />
                </svg>
                <svg v-if="rating.status !== 'ok'" class="eye right">
                  <use xlink:href="#eye" />
                </svg>
                <svg v-if="rating.status !== 'ok' && rating.status !== 'happy'" class="mouth">
                  <use xlink:href="#mouth" />
                </svg>
              </div>
            </li>
            <div :style="'color: ' + rating.color">{{ rating.text }}</div>
          </div>
        </ul>
      </div>
      <!-- This SVG is used in style/rating-emojis.scss, and is used for the emojis faces -->
      <svg xmlns="http://www.w3.org/2000/svg" style="display: none;">
        <symbol id="eye" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 7 4">
          <path d="M1,1 C1.83333333,2.16666667 2.66666667,2.75 3.5,2.75 C4.33333333,2.75 5.16666667,2.16666667 6,1" />
        </symbol>
        <symbol id="mouth" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 7">
          <path d="M1,5.5 C3.66666667,2.5 6.33333333,1 9,1 C11.6666667,1 14.3333333,2.5 17,5.5" />
        </symbol>
      </svg>
      <div class="meeting-rating__content">
        <h2 class="meeting-rating__subtitle">
          {{ $t("message.MEETING_ACTIONS") }}
        </h2>
        <div v-for="(action, index) in actions" :key="action.id" class="meeting-action">
          <div class="text">
            <EditableLabel
              v-model="action.text"
              :placeholder="$t('message.MEETING_ACTION_PH')"
              @input="actionEdited(action, $event)"
            />
          </div>
          <div
            class="archive-button generic-border generic-border--text-btn generic-border--grey"
            @click="archiveAction(index)"
          >
            <Icon>{{ archivesBox }}</Icon>
            {{ $t("message.ARCHIVE") }}
          </div>
        </div>
        <div class="add-button generic-border generic-border--text-btn generic-border--grey" @click="addAction()">
          <Icon>{{ addCircleMedium }}</Icon>
          {{ $t("message.ADD_AN_ACTION") }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import toastr from 'toastr';
// Components
import EditableLabel from '@/components/editable-label.vue';
import Icon from '@/components/icon.vue';
// Import socket handling utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
import { ratings } from '@/utils/meeting-utils';
// Assets
import { addCircleMedium, archivesBox } from '@/assets/icons';

export default {
  name: 'MeetingRating',
  components: {
    EditableLabel,
    Icon,
  },
  mixins: [mixinSocketHandler],
  data() {
    return {
      addCircleMedium,
      archivesBox,
      ratings: ratings.map(r => ({ ...r, text: this.$t(r.textTr) })),
      currentRating: -1,
      actions: [],
      // Properties used by other components. See src/views/readme.md
      pageTitle: this.$t('message.MEETING_RATING'),
      iaTeachText: this.$t('sensei.TEACHER_TEXT'),
      iaTeachBtns: [this.teacherBtn('MEETING_EVALUATION')],
    };
  },

  computed: {
    meeting() {
      return this.$store.state.meeting.meetingData;
    },
  },

  watch: {
    meeting() {
      this.refresh();
    },
  },

  created() {
    this.refresh();
    this.handleSocketMessage(SocketMessages.CC_MSGS.MEETING_RATING, () => {
      this.refresh();
    });
  },

  methods: {
    // Retrive current rating of the meeting and this meeting's actions
    refresh() {
      if (!this.meeting) return;
      this.App.post('/server/meetings/getRating.php', { meetingId: this.meeting.id }, (d) => {
        if (!isNaN(d)) {
          const n = Number(d);
          if (n >= 1 && n <= 4) {
            this.currentRating = n;
          }
        }
      }, true);

      this.App.post('/server/meetings/getActions.php', { planning: this.meeting.planningId }, (d) => {
        if (Array.isArray(d)) {
          this.actions = d;
        }
      });
    },
    rate(rating) {
      this.App.post('/server/meetings/rate.php', { meetingId: this.meeting.id, rating: rating.rating }, (d) => {
        if (d === 1) {
          toastr.success(this.$t('message.RATING_UPDATED'));
          this.currentRating = rating.rating;
          this.sendSocketMessage(SocketMessages.CC_MSGS.MEETING_RATING);
        }
      });
    },
    addAction() {
      this.App.post('/server/meetings/addAction.php', { planning: this.meeting.planningId }, (d) => {
        if (typeof d === 'object') {
          this.actions.push(d);
        }
      });
    },
    actionEdited(action, text) {
      this.App.post('/server/meetings/editAction.php', { id: action.id, text }, (d) => {
        if (typeof d === 'object') {
          this.sendSocketMessage(SocketMessages.CC_MSGS.MEETING_RATING);
        }
      });
    },
    archiveAction(index) {
      this.App.post('/server/meetings/archiveAction.php', { id: this.actions[index].id }, (d) => {
        if (d === 1) {
          this.actions.splice(index, 1);
          this.sendSocketMessage(SocketMessages.CC_MSGS.MEETING_RATING);
        }
      });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";
@import "@/assets/scss/rating-emojis.scss";

.meeting-rating {
  overflow: hidden;

  &__subtitle {
    text-align: center;
  }

  &__content {
    background: white;
    border-radius: $main-border-radius;
    margin: $page-content-padding;
    width: calc(100% - #{$page-content-padding} * 2);
    display: flex;
    text-align: center;
    justify-content: center;
    align-items: center;
    flex-direction: column;
  }

  .basic-gray-section {
    height: 46%;
    margin: 2% 0px;
    text-align: center;
  }
  .f-h-below-title {
    overflow: auto;
    padding: 0px;
    box-sizing: border-box;
  }

  .meeting-ratings-mark {
    margin: 2em 1em;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
  }

  .meeting-action {
    width: 50%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1em 0;
    border-bottom: 1px solid $color-border-grey-dark;

    & .text {
      width: 80%;
      word-wrap: break-word;
      text-align: left;
    }
  }

  .add-button {
    margin: 1em auto;
  }

  .archive-button {
    min-width: fit-content;
  }
}

@media (max-width: $smartphone-landscape) {
  .meeting-rating {

    .meeting-action {
      width: 100%;
    }
  }
}
</style>
